import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Grid,
  TextField,
  InputAdornment,
  Chip,
  IconButton,
} from '@mui/material';
import {
  Search,
  Clear,
  AccountTree,
  Layers,
  People,
  ReceiptLong,
  Tune,
  Engineering,
  TipsAndUpdates,
  NotificationsActive,
  DesignServices,
  ArrowForward,
  Apps,
  Description,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from 'Shared/Security/Domain/Contexts/authContext';
import {
  ORGANIGRAMA,
  EXTRAS,
  CORPORATE_DOCUMENTS,
} from 'Shared/Routing/Application/Constants/PathsConstant';
import {
  PRIVATE_USER_LIST_USERS,
  PRIVATE_USER_VALIDATION_ORGCHARTS,
  IMPROVEMENT_CHANNEL,
  DELEGATION_SERVICE,
} from 'User/Application/Constants/PathsConstant';
import { PRIVATE_ADMIN_EXTRA_CONFIG } from 'Extra/AfterSalesExtra/Application/Constants/PathsConstant';
import { PRIVATE_ADMIN_EXTRA_INSTALLATION } from 'Extra/InstallationExtra/Application/Constants/PathsConstant';
import { PRIVATE_CS_ALERT_CONFIGURATION } from 'CS/Application/Constants/PathsConstant';
import {
  ADMIN_CS,
  READ_USERS,
  WRITE_USERS,
  GESTOR_DELEGACIONES_SERVICIOS,
  DELEGADO_DELEGACIONES_SERVICIOS,
  EXTRAS as EXTRAS_PERMISSION,
  EXTRAS_EMPLOYE,
  EXTRAS_VALIDATOR,
  ADMIN_ORGCHART,
  ORGCHART,
} from 'Shared/Security/Application/Constants/PermissionsConstant';

const PortalUserView = () => {
  const navigate = useNavigate();
  const { checkPermission, isAdmin } = useAuthContext();
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');

  const canAdminOrgChart = isAdmin() || checkPermission(ADMIN_ORGCHART) || checkPermission(ORGCHART);
  const hasValidationOrgChartPermission = canAdminOrgChart || checkPermission(EXTRAS_VALIDATOR);

  // Define modules grouped by functionality with brand-consistent gradients & badges
  const sections = useMemo(() => {
    const list = [
      {
        id: 'organigrama',
        title: 'Organigrama y Personas',
        color: '#257eb9',
        tint: '#e0f2fe',
        icon: <AccountTree sx={{ fontSize: '1.2rem', color: '#257eb9' }} />,
        items: [
          {
            id: 'org-empresa',
            title: 'Organigrama de Empresa',
            path: ORGANIGRAMA,
            icon: <AccountTree sx={{ fontSize: '1.45rem', color: '#ffffff' }} />,
            gradient: 'linear-gradient(135deg, #257eb9 0%, #38bdf8 100%)',
            badge: 'General',
            badgeColor: '#0284c7',
          },
          ...(hasValidationOrgChartPermission
            ? [
                {
                  id: 'org-validaciones',
                  title: 'Organigramas de validaciones',
                  path: PRIVATE_USER_VALIDATION_ORGCHARTS,
                  icon: <Layers sx={{ fontSize: '1.45rem', color: '#ffffff' }} />,
                  gradient: 'linear-gradient(135deg, #4f46e5 0%, #818cf8 100%)',
                  badge: 'Validaciones',
                  badgeColor: '#4338ca',
                },
              ]
            : []),
          ...(checkPermission(WRITE_USERS) || checkPermission(READ_USERS)
            ? [
                {
                  id: 'org-usuarios',
                  title: 'Gestión de Usuarios',
                  path: PRIVATE_USER_LIST_USERS,
                  icon: <People sx={{ fontSize: '1.45rem', color: '#ffffff' }} />,
                  gradient: 'linear-gradient(135deg, #0d9488 0%, #2dd4bf 100%)',
                  badge: 'Gestión',
                  badgeColor: '#0f766e',
                },
              ]
            : []),
        ],
      },
      {
        id: 'extras',
        title: 'Gestión de Extras',
        color: '#d97706',
        tint: '#fef3c7',
        icon: <ReceiptLong sx={{ fontSize: '1.2rem', color: '#d97706' }} />,
        items: [
          ...(checkPermission(EXTRAS_PERMISSION) ||
          checkPermission(EXTRAS_EMPLOYE) ||
          checkPermission(EXTRAS_VALIDATOR)
            ? [
                {
                  id: 'extras-user',
                  title: 'Extras',
                  path: EXTRAS,
                  icon: <ReceiptLong sx={{ fontSize: '1.45rem', color: '#ffffff' }} />,
                  gradient: 'linear-gradient(135deg, #257eb9 0%, #60a5fa 100%)',
                  badge: 'Operativa',
                  badgeColor: '#0284c7',
                },
              ]
            : []),
          ...(checkPermission(EXTRAS_PERMISSION) || isAdmin()
            ? [
                {
                  id: 'extras-config-posventa',
                  title: 'Configuración Extras Posventa',
                  path: PRIVATE_ADMIN_EXTRA_CONFIG,
                  icon: <Tune sx={{ fontSize: '1.45rem', color: '#ffffff' }} />,
                  gradient: 'linear-gradient(135deg, #d97706 0%, #fbbf24 100%)',
                  badge: 'Configuración',
                  badgeColor: '#b45309',
                },
                {
                  id: 'extras-installation',
                  title: 'Instalación Extra',
                  path: PRIVATE_ADMIN_EXTRA_INSTALLATION,
                  icon: <Engineering sx={{ fontSize: '1.45rem', color: '#ffffff' }} />,
                  gradient: 'linear-gradient(135deg, #ea580c 0%, #fb923c 100%)',
                  badge: 'Instalación',
                  badgeColor: '#c2410c',
                },
              ]
            : []),
        ],
      },
      {
        id: 'mejora',
        title: 'Canal de Mejora',
        color: '#ca8a04',
        tint: '#fef9c3',
        icon: <TipsAndUpdates sx={{ fontSize: '1.2rem', color: '#ca8a04' }} />,
        items: [
          {
            id: 'canal-mejora',
            title: 'Canal de Mejora',
            path: IMPROVEMENT_CHANNEL,
            icon: <TipsAndUpdates sx={{ fontSize: '1.45rem', color: '#ffffff' }} />,
            gradient: 'linear-gradient(135deg, #ca8a04 0%, #facc15 100%)',
            badge: 'Innovación',
            badgeColor: '#a16207',
          },
        ],
      },
      ...(checkPermission(ADMIN_CS)
        ? [
            {
              id: 'cs',
              title: 'Configuración Alertas CS',
              color: '#e11d48',
              tint: '#ffe4e6',
              icon: <NotificationsActive sx={{ fontSize: '1.2rem', color: '#e11d48' }} />,
              items: [
                {
                  id: 'cs-alert-config',
                  title: 'Configuración Alertas CS',
                  path: PRIVATE_CS_ALERT_CONFIGURATION,
                  icon: <NotificationsActive sx={{ fontSize: '1.45rem', color: '#ffffff' }} />,
                  gradient: 'linear-gradient(135deg, #e11d48 0%, #fb7185 100%)',
                  badge: 'Customer Service',
                  badgeColor: '#be123c',
                },
              ],
            },
          ]
        : []),
      ...(checkPermission(GESTOR_DELEGACIONES_SERVICIOS) ||
      checkPermission(DELEGADO_DELEGACIONES_SERVICIOS)
        ? [
            {
              id: 'delegation-services',
              title: 'Delegación de Servicios',
              color: '#7c3aed',
              tint: '#ede9fe',
              icon: <DesignServices sx={{ fontSize: '1.2rem', color: '#7c3aed' }} />,
              items: [
                {
                  id: 'del-services-main',
                  title: 'Delegación de Servicios',
                  path: DELEGATION_SERVICE,
                  icon: <DesignServices sx={{ fontSize: '1.45rem', color: '#ffffff' }} />,
                  gradient: 'linear-gradient(135deg, #7c3aed 0%, #a78bfa 100%)',
                  badge: 'Servicios',
                  badgeColor: '#6d28d9',
                },
              ],
            },
          ]
        : []),
      {
        id: 'documentos',
        title: 'Documentación Corporativa',
        color: '#0284c7',
        tint: '#e0f2fe',
        icon: <Description sx={{ fontSize: '1.2rem', color: '#0284c7' }} />,
        items: [
          {
            id: 'docs-corporativos',
            title: 'Documentos y Catálogos',
            path: CORPORATE_DOCUMENTS,
            icon: <Description sx={{ fontSize: '1.45rem', color: '#ffffff' }} />,
            gradient: 'linear-gradient(135deg, #0284c7 0%, #38bdf8 100%)',
            badge: 'Biblioteca',
            badgeColor: '#0369a1',
          },
        ],
      },
    ];

    return list.filter((sec) => sec.items && sec.items.length > 0);
  }, [checkPermission, isAdmin, hasValidationOrgChartPermission]);

  // Total count of accessible tools
  const totalItemsCount = useMemo(() => {
    return sections.reduce((acc, s) => acc + s.items.length, 0);
  }, [sections]);

  // Filter sections by search term & selected category
  const filteredSections = useMemo(() => {
    let result = sections;
    if (activeCategory !== 'all') {
      result = result.filter((s) => s.id === activeCategory);
    }

    if (!searchTerm.trim()) return result;
    const term = searchTerm.toLowerCase();

    return result
      .map((section) => {
        const matchesSection = section.title.toLowerCase().includes(term);
        const matchingItems = section.items.filter(
          (item) =>
            matchesSection ||
            item.title.toLowerCase().includes(term) ||
            (item.badge && item.badge.toLowerCase().includes(term))
        );

        return {
          ...section,
          items: matchingItems,
        };
      })
      .filter((section) => section.items.length > 0);
  }, [sections, activeCategory, searchTerm]);

  return (
    <Box
      sx={{
        p: { xs: 1.5, sm: 2, md: 2.5 },
        bgcolor: '#f1f5f9',
        minHeight: '100%',
        width: '100%',
        boxSizing: 'border-box',
      }}
    >
      <Box sx={{ maxWidth: '1440px', mx: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Top Header Card */}
        <Box
          sx={{
            borderRadius: '20px',
            bgcolor: '#ffffff',
            border: '1px solid #e2e8f0',
            boxShadow: '0 4px 16px rgba(0,0,0,0.04)',
            p: { xs: 2, sm: 2.2 },
            display: 'flex',
            flexDirection: 'column',
            gap: 1.8,
          }}
        >
          {/* Top Line: Title & Search */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'space-between',
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: 1.5,
            }}
          >
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.4 }}>
                <Chip
                  icon={<Apps sx={{ fontSize: '0.95rem', color: '#257eb9 !important' }} />}
                  label="PORTAL DE SERVICIOS"
                  size="small"
                  sx={{
                    bgcolor: '#e0f2fe',
                    color: '#0369a1',
                    fontWeight: 700,
                    fontSize: '0.72rem',
                    letterSpacing: 0.5,
                    borderRadius: '8px',
                    height: '24px',
                  }}
                />
              </Box>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 800,
                  color: '#1e293b',
                  fontSize: { xs: '1.25rem', sm: '1.45rem' },
                  letterSpacing: -0.4,
                }}
              >
                Portal Interno
              </Typography>
            </Box>

            {/* Quick Search */}
            <TextField
              size="small"
              placeholder="Buscar funcionalidad o trámite..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: '#257eb9', fontSize: '1.2rem' }} />
                  </InputAdornment>
                ),
                endAdornment: searchTerm ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchTerm('')}>
                      <Clear sx={{ fontSize: '1rem', color: '#94a3b8' }} />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
              sx={{
                width: { xs: '100%', sm: '320px' },
                '& .MuiOutlinedInput-root': {
                  borderRadius: '16px',
                  bgcolor: '#f8fafc',
                  fontSize: '0.85rem',
                  height: '40px',
                  '& fieldset': { borderColor: '#cbd5e1' },
                  '&:hover fieldset': { borderColor: '#257eb9' },
                  '&.Mui-focused fieldset': { borderColor: '#257eb9' },
                },
              }}
            />
          </Box>

          {/* Interactive Category Filter Pills */}
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              flexWrap: 'wrap',
              alignItems: 'center',
              pt: 1,
              borderTop: '1px solid #f1f5f9',
            }}
          >
            <Chip
              label={`Todos (${totalItemsCount})`}
              onClick={() => setActiveCategory('all')}
              sx={{
                fontWeight: 700,
                fontSize: '0.78rem',
                cursor: 'pointer',
                borderRadius: '10px',
                height: '30px',
                bgcolor: activeCategory === 'all' ? '#257eb9' : '#f8fafc',
                color: activeCategory === 'all' ? '#ffffff' : '#475569',
                border: activeCategory === 'all' ? '1px solid #257eb9' : '1px solid #e2e8f0',
                boxShadow: activeCategory === 'all' ? '0 2px 8px rgba(37, 126, 185, 0.3)' : 'none',
                transition: 'all 0.18s ease',
                '&:hover': {
                  bgcolor: activeCategory === 'all' ? '#1d6594' : '#edf2f7',
                },
              }}
            />
            {sections.map((sec) => {
              const isSelected = activeCategory === sec.id;
              return (
                <Chip
                  key={sec.id}
                  label={`${sec.title} (${sec.items.length})`}
                  onClick={() => setActiveCategory(sec.id)}
                  sx={{
                    fontWeight: 700,
                    fontSize: '0.78rem',
                    cursor: 'pointer',
                    borderRadius: '10px',
                    height: '30px',
                    bgcolor: isSelected ? '#257eb9' : '#f8fafc',
                    color: isSelected ? '#ffffff' : '#475569',
                    border: isSelected ? '1px solid #257eb9' : '1px solid #e2e8f0',
                    boxShadow: isSelected ? '0 2px 8px rgba(37, 126, 185, 0.3)' : 'none',
                    transition: 'all 0.18s ease',
                    '&:hover': {
                      bgcolor: isSelected ? '#1d6594' : '#edf2f7',
                    },
                  }}
                />
              );
            })}
          </Box>
        </Box>

        {/* Content Sections */}
        {filteredSections.length === 0 ? (
          <Box
            sx={{
              bgcolor: '#ffffff',
              borderRadius: '20px',
              p: 5,
              textAlign: 'center',
              border: '1px solid #e2e8f0',
            }}
          >
            <Typography variant="h6" sx={{ color: '#1e293b', fontWeight: 700, mb: 1 }}>
              No se encontraron funcionalidades
            </Typography>
            <Typography sx={{ color: '#64748b', fontSize: '0.9rem' }}>
              No hay elementos que coincidan con los filtros aplicados.
            </Typography>
          </Box>
        ) : (
          filteredSections.map((section) => (
            <Box key={section.id} sx={{ display: 'flex', flexDirection: 'column', gap: 1.2 }}>
              {/* Section Header Line */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, px: 0.5, mt: 0.5 }}>
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: '8px',
                    bgcolor: section.tint || '#e0f2fe',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {section.icon}
                </Box>
                <Typography
                  sx={{
                    fontWeight: 800,
                    color: '#1e293b',
                    fontSize: '0.98rem',
                    letterSpacing: -0.2,
                  }}
                >
                  {section.title}
                </Typography>
                <Chip
                  label={`${section.items.length}`}
                  size="small"
                  sx={{
                    height: '20px',
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    bgcolor: '#ffffff',
                    border: '1px solid #cbd5e1',
                    color: '#475569',
                    borderRadius: '6px',
                  }}
                />
                <Box sx={{ flex: 1, height: '1px', bgcolor: '#e2e8f0', ml: 1 }} />
              </Box>

              {/* Grid of Modern Action Tiles */}
              <Grid container spacing={1.5}>
                {section.items.map((item) => (
                  <Grid item key={item.id} xs={12} sm={6} md={4} lg={4}>
                    <Box
                      onClick={() => navigate(item.path)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.8,
                        p: { xs: 1.5, sm: 1.8 },
                        borderRadius: '16px',
                        border: '1px solid #e2e8f0',
                        boxShadow: '0 2px 10px rgba(0,0,0,0.03)',
                        bgcolor: '#ffffff',
                        cursor: 'pointer',
                        position: 'relative',
                        minHeight: '74px',
                        transition: 'all 0.22s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: '0 8px 24px rgba(37, 126, 185, 0.14)',
                          borderColor: '#93c5fd',
                          '& .card-title': { color: '#257eb9' },
                          '& .card-arrow-box': {
                            bgcolor: '#257eb9',
                            color: '#ffffff',
                            transform: 'translateX(3px)',
                          },
                        },
                      }}
                    >
                      {/* Gradient Icon Container */}
                      <Box
                        sx={{
                          width: 46,
                          height: 46,
                          borderRadius: '13px',
                          background: item.gradient || 'linear-gradient(135deg, #257eb9 0%, #38bdf8 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          boxShadow: '0 4px 12px rgba(37, 126, 185, 0.22)',
                        }}
                      >
                        {item.icon}
                      </Box>

                      {/* Content: Badge Tag & Title */}
                      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                        {item.badge && (
                          <Typography
                            sx={{
                              fontSize: '0.68rem',
                              fontWeight: 700,
                              letterSpacing: 0.6,
                              textTransform: 'uppercase',
                              color: item.badgeColor || '#0284c7',
                            }}
                          >
                            {item.badge}
                          </Typography>
                        )}
                        <Typography
                          className="card-title"
                          sx={{
                            fontWeight: 700,
                            fontSize: '0.94rem',
                            color: '#1e293b',
                            lineHeight: 1.3,
                            transition: 'color 0.2s ease',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {item.title}
                        </Typography>
                      </Box>

                      {/* Circular Action Arrow */}
                      <Box
                        className="card-arrow-box"
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          bgcolor: '#f1f5f9',
                          color: '#94a3b8',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          transition: 'all 0.2s ease',
                        }}
                      >
                        <ArrowForward sx={{ fontSize: '0.95rem' }} />
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>
          ))
        )}
      </Box>
    </Box>
  );
};

export default PortalUserView;
